import { useState, useEffect, useRef } from 'react';
import Hls from 'hls.js';
import { useTranslation } from 'react-i18next';
import { auth } from '../../lib/firebase';
import { fetchVideoToken } from '../../lib/api';

const CF_CUSTOMER_SUBDOMAIN = import.meta.env.VITE_CF_STREAM_CUSTOMER_SUBDOMAIN;

function getYouTubeEmbedUrl(src) {
  const match = src.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  return match ? `https://www.youtube.com/embed/${match[1]}?rel=0&modestbranding=1` : null;
}

function get3rdPartyEmbedUrl(src) {
  const ytUrl = getYouTubeEmbedUrl(src);
  return ytUrl ?? src;
}

export default function DrillVideoPlayer({ drill, schoolId }) {
  const { video } = drill;
  const { t } = useTranslation();

  const [manifestUrl, setManifestUrl] = useState(null);
  const [tokenError, setTokenError] = useState(null);
  const [loadingToken, setLoadingToken] = useState(false);
  const [isPseudoFullscreen, setIsPseudoFullscreen] = useState(false);
  const videoRef = useRef(null);
  const hlsRef = useRef(null);
  const iframeRef = useRef(null);

  useEffect(() => {
    if (video?.type !== 'cloudflare') return;

    if (video.protected) {
      setLoadingToken(true);
      auth.currentUser.getIdToken()
        .then(idToken => fetchVideoToken(video.src, schoolId, idToken))
        .then(({ manifestUrl: url }) => setManifestUrl(url))
        .catch(err => setTokenError(err.message))
        .finally(() => setLoadingToken(false));
    } else {
      setManifestUrl(
        `https://${CF_CUSTOMER_SUBDOMAIN}.cloudflarestream.com/${video.src}/manifest/video.m3u8`
      );
    }
  }, [video?.src, video?.type, video?.protected, schoolId]);

  useEffect(() => {
    if (!manifestUrl || !videoRef.current) return;

    const videoEl = videoRef.current;

    if (videoEl.canPlayType('application/vnd.apple.mpegurl')) {
      videoEl.src = manifestUrl;
    } else if (Hls.isSupported()) {
      const hls = new Hls({ enableWorker: true });
      hls.loadSource(manifestUrl);
      hls.attachMedia(videoEl);
      hlsRef.current = hls;
    }

    return () => {
      if (hlsRef.current) {
        hlsRef.current.destroy();
        hlsRef.current = null;
      }
      videoEl.src = '';
    };
  }, [manifestUrl]);

  if (video.type === '3rd-party') {
    const handleFullscreen = async () => {
      const el = iframeRef.current;
      if (!el) return;
      const requestFS = el.requestFullscreen || el.webkitRequestFullscreen || el.mozRequestFullScreen;
      if (requestFS) {
        try {
          await requestFS.call(el);
          return;
        } catch (_) {
          // iOS doesn't support requestFullscreen on iframes — fall through
        }
      }
      setIsPseudoFullscreen(true);
    };

    return (
      <div className={isPseudoFullscreen ? 'fixed inset-0 z-[9999] bg-black flex flex-col' : 'w-full flex flex-col gap-2'}>
        <div className={isPseudoFullscreen ? 'flex-1' : 'w-full aspect-video rounded-xl overflow-hidden bg-black'}>
          <iframe
            ref={iframeRef}
            src={get3rdPartyEmbedUrl(video.src)}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; fullscreen"
            allowFullScreen="true"
            title={drill.nameEn}
          />
        </div>
        {video.showFullScreenBtn && (
          isPseudoFullscreen ? (
            <button
              onClick={() => setIsPseudoFullscreen(false)}
              className="w-full px-6 py-4 bg-gray-800 text-white font-bold text-lg md:text-2xl min-h-[52px] flex items-center justify-center"
            >
              ✕ Close
            </button>
          ) : (
            <button
              onClick={handleFullscreen}
              className="w-full px-6 py-4 btn-primary text-white rounded-2xl font-bold text-lg md:text-2xl min-h-[52px] flex items-center justify-center shadow-lg hover:shadow-xl hover:scale-105 active:scale-95 transition-all"
            >
              Full Screen
            </button>
          )
        )}
      </div>
    );
  }

  return (
    <div className="w-full aspect-video rounded-xl overflow-hidden bg-black flex items-center justify-center">
      {loadingToken ? (
        <div className="text-white text-sm">{t('common.loading')}</div>
      ) : tokenError ? (
        <div className="text-red-400 text-sm px-4 text-center">
          {t('video.token_error', 'Could not load video')} — {tokenError}
        </div>
      ) : (
        <video
          ref={videoRef}
          controls
          playsInline
          webkit-playsinline="true"
          className="w-full h-full object-contain"
        />
      )}
    </div>
  );
}
